import { useEffect, useRef, useState } from "react";
import { uploadAttachments } from "../api/backend.js";
import { Alert, Card } from "./ui.jsx";
import styles from "./AttachmentPicker.module.css";

const ACCEPT = "image/png,image/jpeg,image/webp,image/heic,video/mp4,video/quicktime,application/pdf";
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Fotos/vídeo/PDF do produto. Envia na hora (para não pesar no "Confirmar")
 * e devolve a lista já enviada via onChange.
 */
const AttachmentPicker = ({ required, maxFiles = 3, value = [], onChange, defectOnly }) => {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState({});
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => () => Object.values(previews).forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const handleFiles = async (fileList) => {
    setMessage("");
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (value.length + files.length > maxFiles) {
      setMessage(`Você pode enviar até ${maxFiles} arquivo(s).`);
      return;
    }
    const tooBig = files.find((file) => file.size > MAX_BYTES);
    if (tooBig) {
      setMessage(`"${tooBig.name}" passa de 10 MB.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadAttachments(files);
      const nextPreviews = { ...previews };
      uploaded.forEach((meta, index) => {
        const source = files[index];
        if (source && source.type.startsWith("image/")) nextPreviews[meta.fileName] = URL.createObjectURL(source);
      });
      setPreviews(nextPreviews);
      onChange([...value, ...uploaded]);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Não foi possível enviar. Tente de novo.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (fileName) => onChange(value.filter((file) => file.fileName !== fileName));

  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <h3>Fotos do produto {required ? <span className={styles.required}>obrigatório</span> : <span className={styles.optional}>opcional</span>}</h3>
        <p>
          {defectOnly && required
            ? "Mostre o defeito ou o problema: ajuda a loja a aprovar mais rápido."
            : "Fotos ou vídeo do produto e da etiqueta ajudam a loja a analisar mais rápido."}
        </p>
      </div>

      {value.length > 0 ? (
        <ul className={styles.list}>
          {value.map((file) => (
            <li key={file.fileName} className={styles.item}>
              {previews[file.fileName] ? (
                <img src={previews[file.fileName]} alt="" />
              ) : (
                <span className={styles.fileIcon} aria-hidden="true">{(file.mimeType || "").includes("pdf") ? "PDF" : "▶"}</span>
              )}
              <span className={styles.name}>{file.originalName}</span>
              <button type="button" className={styles.remove} onClick={() => remove(file.fileName)} aria-label={`Remover ${file.originalName}`}>
                Remover
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {value.length < maxFiles ? (
        <label
          className={`${styles.drop} ${uploading ? styles.busy : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            handleFiles(event.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            disabled={uploading}
            onChange={(event) => handleFiles(event.target.files)}
          />
          <strong>{uploading ? "Enviando..." : "Adicionar fotos ou vídeo"}</strong>
          <span>JPG, PNG, HEIC, MP4 ou PDF · até 10 MB · {value.length}/{maxFiles}</span>
        </label>
      ) : null}

      {message ? <Alert>{message}</Alert> : null}
    </Card>
  );
};

export default AttachmentPicker;
